<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">账号管理</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 个账号</span>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="登录账号">
            <el-input v-model="query.keyword" placeholder="账号关键字" clearable style="width: 180px" />
          </el-form-item>
          <el-form-item label="所属公司">
            <el-select v-model="query.company_id" placeholder="全部" clearable style="width: 170px">
              <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 110px">
              <el-option label="正常" :value="1" />
              <el-option label="禁用" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增账号</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <el-table-column prop="username" label="登录账号" min-width="140"/>
        <el-table-column label="关联人员" width="110">
          <template slot-scope="scope">
            <span v-if="scope.row.person_id">{{ scope.row.person_name }}</span>
            <span v-else class="cell-muted">纯账号</span>
          </template>
        </el-table-column>
        <el-table-column label="所属公司" min-width="160">
          <template slot-scope="scope">{{ scope.row.company_name || '—' }}</template>
        </el-table-column>
        <el-table-column label="角色" min-width="150">
          <template slot-scope="scope">
            <template v-if="scope.row.roles && scope.row.roles.length">
              <el-tag v-for="r in scope.row.roles" :key="r.id" size="mini" effect="plain" class="role-tag">{{ r.name }}</el-tag>
            </template>
            <span v-else class="cell-muted">未分配</span>
          </template>
        </el-table-column>
        <el-table-column label="超级管理员" width="100" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.is_super === 1" type="danger" size="mini" effect="plain">是</el-tag>
            <span v-else class="cell-muted">否</span>
          </template>
        </el-table-column>
        <el-table-column prop="last_login_at" label="最近登录" width="150"/>
        <el-table-column prop="last_login_ip" label="最近登录IP" width="130"/>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.status"
              :active-value="1"
              :inactive-value="0"
              @change="val => handleStatusChange(scope.row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="210" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="mini" @click="handleResetPwd(scope.row)">重置密码</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <el-dialog :title="form.id ? '编辑账号' : '新增账号'" :visible.sync="dialogVisible" width="680px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="110px">
        <el-form-item label="登录账号" prop="username">
          <el-input v-model="form.username" :disabled="!!form.id" maxlength="64" />
        </el-form-item>
        <!-- 初始密码由后端随机生成并在提交成功后一次性回显，前端不收集、不提交密码 -->
        <el-form-item label="所属公司" prop="company_id">
          <el-select v-model="form.company_id" style="width: 100%" @change="handleCompanyChange">
            <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <!-- 关联人员跟着「所属公司」联动：未选公司=全部人员；选了公司=只出该公司人员；选人后自动回填所属公司 -->
        <el-form-item label="关联人员">
          <el-select v-model="form.person_id" clearable filterable placeholder="纯账号可不选" style="width: 100%" @change="handlePersonChange">
            <el-option v-for="p in persons" :key="p.id" :label="personLabel(p)" :value="p.id" />
          </el-select>
        </el-form-item>
        <!-- 角色：多选，菜单权限=所勾角色权限并集；超管账号不受角色限制（天然全量）。
             选项=启用中且「系统预置或本公司」的角色，super_admin 不外放 -->
        <el-form-item label="角色">
          <el-select
            v-model="form.role_ids"
            multiple
            clearable
            :disabled="form.is_super === 1"
            placeholder="可多选，决定菜单与操作权限"
            style="width: 100%"
          >
            <el-option v-for="r in roleOptions" :key="r.id" :label="r.name" :value="r.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="超级管理员">
          <el-switch v-model="form.is_super" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="0" active-text="正常" inactive-text="禁用" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" @click="submit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getPersonList,
  getAdminList,
  createAdmin,
  updateAdmin,
  deleteAdmin,
  changeAdminStatus,
  resetAdminPassword,
  getCompanyOptions,
  getRoleList
} from '@/api/system'

const emptyForm = () => ({
  id: 0, company_id: 1, person_id: undefined, username: '', is_super: 0, role_ids: [], status: 1
})

export default {
  name: 'AdminManage',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      query: { keyword: '', company_id: '', status: '' },
      dialogVisible: false,
      form: emptyForm(),
      persons: [], // 关联人员下拉（走人员列表接口，部门下拉待对应后端建成后再换）
      companies: [], // 所属公司下拉（走公司管理接口）
      roles: [], // 角色全量（启用中），按公司过滤后做下拉
      rules: {
        username: [{ required: true, message: '请输入登录账号', trigger: 'blur' }],
        company_id: [{ required: true, message: '请选择所属公司', trigger: 'change' }]
      }
    }
  },
  computed: {
    // 角色下拉：启用中、非 super_admin（不走角色授权）、系统预置或本公司
    roleOptions() {
      const cid = Number(this.form.company_id) || 0
      return this.roles.filter(r =>
        Number(r.status) === 1 &&
        r.code !== 'super_admin' &&
        (Number(r.company_id) === 0 || Number(r.company_id) === cid)
      )
    }
  },
  watch: {
    // 换公司后，已勾的本公司角色可能越界，清掉不属于新公司的
    'form.company_id'() {
      const allow = this.roleOptions.map(r => r.id)
      this.form.role_ids = (this.form.role_ids || []).filter(id => allow.indexOf(id) !== -1)
    },
    // 开超管即清空已选角色：超管天然全量，角色关联无意义，留着反而在关回非超管时悄悄生效
    'form.is_super'(val) {
      if (val === 1 && this.form.role_ids && this.form.role_ids.length) {
        this.form.role_ids = []
      }
    }
  },
  created() {
    this.loadData()
    this.loadCompanies()
  },
  methods: {
    // 服务端分页 + 筛选：只传非空查询条件
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.company_id !== '' && this.query.company_id !== null) params.company_id = this.query.company_id
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status

      getAdminList(params).then(res => {
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    loadCompanies() {
      // 所属公司下拉：走公司管理 options 接口（仅正常状态）
      getCompanyOptions().then(res => {
        this.companies = (res.data || [])
      })
    },
    loadPersons(companyId) {
      // 关联人员下拉：跟着「所属公司」联动——传了公司只拉该公司人员，不传则拉全部
      // 拉第一页大页容量（人员量小够用）
      const params = { page: 1, page_size: 100 }
      if (companyId !== '' && companyId !== null && companyId !== undefined) {
        params.company_id = companyId
      }
      const previous = this.persons || []
      getPersonList(params).then(res => {
        const list = (res.data && res.data.list) || []
        // 编辑历史数据兜底：已选人员不在该公司范围内时保留一条，避免下拉显示成 id
        const pid = Number(this.form.person_id) || 0
        if (pid > 0 && !list.some(p => Number(p.id) === pid)) {
          const kept = previous.find(p => Number(p.id) === pid)
          if (kept) list.unshift(kept)
        }
        this.persons = list
      })
    },
    // 公司换选：清掉已选人员，按新公司重拉人员下拉
    handleCompanyChange(companyId) {
      this.form.person_id = undefined
      this.loadPersons(companyId)
    },
    // 选中人员：把所属公司带成该人员所在公司，并按该公司重拉下拉（选项与已选保持一致）
    handlePersonChange(personId) {
      if (!personId) return
      const person = this.persons.find(p => Number(p.id) === Number(personId))
      if (!person) return
      const companyId = Number(person.company_id) || 0
      if (companyId > 0 && companyId !== Number(this.form.company_id)) {
        this.form.company_id = companyId
      }
      this.loadPersons(companyId || '')
    },
    // 人员下拉文案：姓名 +（公司），避免跨公司重名分不清
    personLabel(person) {
      const company = this.companies.find(c => Number(c.id) === Number(person.company_id))
      return company ? `${person.name}（${company.name}）` : person.name
    },
    loadRoles() {
      // 角色下拉：分页接口拉大页容量（角色量小够用），数据范围后端已过滤（本公司+预置）
      getRoleList({ page: 1, page_size: 100, status: 1 }).then(res => {
        this.roles = (res.data && res.data.list) || []
      })
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', company_id: '', status: '' }
      this.page = 1
      this.loadData()
    },
    handleStatusChange(row, val) {
      changeAdminStatus(row.id, { status: val }).then(() => {
        this.$message.success(val === 1 ? '已启用' : '已禁用')
      }).catch(() => {
        // 失败（如禁用超管/自己被后端拦）回滚开关，错误提示由拦截器统一弹
        row.status = val === 1 ? 0 : 1
      })
    },
    handleAdd() {
      this.form = emptyForm()
      // 默认公司：emptyForm 默认主公司(1)，当前账号选不到主公司时落到第一个可选公司
      const options = this.companies || []
      if (options.length && !options.some(c => Number(c.id) === Number(this.form.company_id))) {
        this.form.company_id = options[0].id
      }
      // 人员下拉按默认公司拉；公司还没加载出来时先取全部，用户选公司后再收敛
      this.loadPersons(options.length ? this.form.company_id : '')
      this.loadRoles()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.loadRoles()
      // 编辑不拉详情接口（列表行字段已够回显）；本表单不含密码，改密码走「重置密码」
      // role_ids 必须在赋值时就带上——Vue 2 对「观察后新增的属性」不建响应式，
      // 后补 this.form.role_ids 会导致多选点选项不更新（点了没反应）
      const { roles, ...rest } = row
      this.form = { ...rest, role_ids: (roles || []).map(r => r.id) }
      // person_id=0（未关联人员）不渲染成 "0"，显示 placeholder
      if (!this.form.person_id) this.form.person_id = undefined
      // 回显兜底：先把当前关联人员放进下拉，异步拉取返回前下拉不会显示成 id
      this.persons = this.form.person_id && row.person_name
        ? [{ id: this.form.person_id, name: row.person_name, company_id: this.form.company_id }]
        : []
      // 人员下拉按该账号所属公司拉，回显的人员才会在选项里（否则显示空白）
      this.loadPersons(this.form.company_id)
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleResetPwd(row) {
      // 重置＝后端恢复为系统默认初始密码（前端不传密码），成功后回显
      this.$confirm(
        `确认将账号「${row.username}」的密码重置为系统默认初始密码？重置后请提醒本人登录并立即修改。`,
        '重置密码',
        { type: 'warning', confirmButtonText: '确认重置' }
      ).then(() => {
        return resetAdminPassword(row.id, {}).then(res => {
          const newPwd = res.data && res.data.password
          if (newPwd) {
            this.$alert(
              `账号「${row.username}」密码已重置为：${newPwd}\n请转交本人并提醒登录后立即修改。`,
              '密码已重置',
              { confirmButtonText: '知道了' }
            )
          } else {
            this.$message.success('密码已重置')
          }
        })
      }).catch(() => {})
    },
    handleDelete(row) {
      this.$confirm(`确认删除账号「${row.username}」？`, '删除确认', { type: 'warning' }).then(() => {
        deleteAdmin(row.id).then(() => {
          this.$message.success('已删除')
          // 当前页删空后回退一页
          if (this.list.length === 1 && this.page > 1) this.page -= 1
          this.loadData()
        })
      }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        if (this.form.id) {
          // username 不可改、密码不在本接口改（走重置密码）；roles 行内对象数组不回传，只交 role_ids
          const { username, roles, ...rest } = this.form
          const data = { ...rest }
          // person_id 未选（undefined）归一化为 0：后端可据此清空关联人员
          data.person_id = data.person_id || 0
          data.role_ids = this.form.role_ids || []
          updateAdmin(this.form.id, data).then(() => {
            this.$message.success('已保存修改')
            this.dialogVisible = false
            this.loadData()
          })
        } else {
          const username = this.form.username
          createAdmin({
            ...this.form,
            person_id: this.form.person_id || 0,
            role_ids: this.form.role_ids || []
          }).then(res => {
            this.dialogVisible = false
            this.loadData()
            // 初始密码由后端常量下发，前端只展示、不收集
            const pwd = res.data && res.data.password
            if (pwd) {
              this.$alert(
                `账号「${username}」初始密码：${pwd}\n为系统默认初始密码，请转交本人并提醒登录后立即修改。`,
                '新增成功',
                { confirmButtonText: '已保存' }
              )
            } else {
              this.$message.success('新增成功')
            }
          })
        }
      })
    }
  }
}
</script>

<style>
/* 角色标签间距 */
.role-tag {
  margin-right: 6px;
  margin-bottom: 2px;
}
</style>
